import {
  ReaderDeviceState,
  ReaderStatus,
  type DeviceStatusSnapshot,
  type PaymentReader,
} from "@/types";

export type ReaderLiveStatusBucket =
  | "online"
  | "unavailable"
  | "offline"
  | "unknown";

export type ReaderLiveStatusInput = Pick<
  PaymentReader,
  "provider" | "status" | "device_status_snapshot"
>;

const AVAILABLE_DEVICE_STATES = new Set<string>(
  Object.values(ReaderDeviceState).filter(
    (state) => state !== ReaderDeviceState.UPDATING_FIRMWARE,
  ),
);

export const KNOWN_READER_DEVICE_STATES = new Set<string>(
  Object.values(ReaderDeviceState),
);

function isSumupPaired(reader: ReaderLiveStatusInput): boolean {
  return (
    reader.provider.toUpperCase() === "SUMUP" &&
    reader.status === ReaderStatus.PAIRED
  );
}

/** SumUp GetReaderStatus snapshot for a paired SumUp reader; otherwise null. */
export function getReaderLiveSnapshot(
  reader: ReaderLiveStatusInput,
): DeviceStatusSnapshot | null {
  return isSumupPaired(reader) ? reader.device_status_snapshot : null;
}

/** Classify a SumUp status snapshot. Null when connectivity is missing. */
export function classifyPairedSumupDeviceStatus(
  snap: DeviceStatusSnapshot | null,
): ReaderLiveStatusBucket | null {
  if (!snap?.status) return null;
  if (snap.status !== "ONLINE") return "offline";
  if (!snap.state || AVAILABLE_DEVICE_STATES.has(snap.state)) return "online";
  return "unavailable";
}

/**
 * Live SumUp status bucket for filters/dots.
 * Null when the reader is not a paired SumUp terminal.
 */
export function readerLiveStatusClassification(
  reader: ReaderLiveStatusInput,
): ReaderLiveStatusBucket | null {
  if (!isSumupPaired(reader)) return null;
  return (
    classifyPairedSumupDeviceStatus(reader.device_status_snapshot) ?? "unknown"
  );
}

export function readerLiveStatusDotClass(
  bucket: ReaderLiveStatusBucket,
): string {
  switch (bucket) {
    case "online":
      return "bg-success-500";
    case "unavailable":
      return "bg-warning-400";
    case "offline":
      return "bg-error-500";
    case "unknown":
      return "bg-surface-400";
  }
}

export function readerLiveStatusBadgeVariant(
  bucket: ReaderLiveStatusBucket,
): "success" | "warning" | "error" | "default" {
  switch (bucket) {
    case "online":
      return "success";
    case "unavailable":
      return "warning";
    case "offline":
      return "error";
    case "unknown":
      return "default";
  }
}

export function readerPairingStatusLabelKey(status: ReaderStatus): string {
  switch (status) {
    case ReaderStatus.CREATED:
      return "page.statusCreated";
    case ReaderStatus.NOT_PAIRED:
      return "page.statusNotPaired";
    case ReaderStatus.PAIRED:
      return "page.statusPaired";
  }
}

/** i18n key under `readers` for the live SumUp state label. */
export function readerLiveStateLabelKey(
  snap: DeviceStatusSnapshot,
  bucket: ReaderLiveStatusBucket,
): string {
  if (snap.state) {
    return KNOWN_READER_DEVICE_STATES.has(snap.state)
      ? `page.deviceState.${snap.state}`
      : "page.deviceState.UNKNOWN";
  }
  return bucket === "offline" ? "page.deviceOffline" : "page.deviceOnline";
}

export function getReaderBatteryClass(level: number): string {
  if (level < 10) return "text-error-600";
  if (level < 25) return "text-warning-600";
  if (level > 70) return "text-success-600";
  return "text-surface-500";
}
